import { useCallback, useMemo } from "react";
import { Stack } from "@/components/layout";
import { SectionHeader } from "@/components/ui";
import { useApiClient } from "@/api/useApiClient";
import { usePageBreadcrumbs } from "@/shell/usePageBreadcrumbs";
import { HubEmbeddedProvider, HubTabs, useHubLoad } from "@/modules/hub-kit/HubKit";
import { CreatorOverviewTab, useCreatorBadges } from "../components/CreatorOverviewTab";
import { CreatorRequirementsTab } from "../components/CreatorRequirementsTab";
import { CreatorCandidatesTab } from "../components/CreatorCandidatesTab";
import { createCreatorsOverviewApi } from "../overviewApi";
import { BirthdayChangeQueuePage, IdentityVerificationQueuePage } from "@/modules/users";
import {
  ApplicationsQueuePage,
  BrandDealsQueuePage,
  CampaignsQueuePage,
  InvitationsQueuePage,
  MembershipsQueuePage,
  ParticipationsQueuePage,
  PaymentsQueuePage,
  PerformanceQueuePage,
  ProfilesQueuePage,
  ProgramsQueuePage,
  TierHistoryQueuePage,
} from "./CreatorQueuePages";

const CRUMBS = [{ label: "HUDDL HQ", to: "/app" }, { label: "Creator Center" }];

/**
 * Creator Center: onboard, verify, support and pay the people who create
 * content and run activities on HUDDL.
 */
export function CreatorHubPage() {
  usePageBreadcrumbs(CRUMBS);
  const client = useApiClient();
  const api = useMemo(() => createCreatorsOverviewApi(client), [client]);
  const load = useCallback(async () => (await api.getOverview()).overview, [api]);
  const overview = useHubLoad(load);
  const badges = useCreatorBadges(overview.data);

  return (
    <Stack gap={24}>
      <SectionHeader
        title="Creator Center"
        description="Onboard, verify, support and pay the people who create content and run activities on HUDDL."
      />
      <HubEmbeddedProvider crumbs={CRUMBS}>
        <HubTabs
          items={[
            {
              id: "overview",
              label: "Overview",
              content: (
                <CreatorOverviewTab
                  data={overview.data}
                  error={overview.error}
                  loading={overview.loading}
                  reload={() => void overview.reload()}
                />
              ),
            },
            {
              id: "applications",
              label: "Applications",
              ...(badges.open ? { badge: badges.open } : {}),
              content: (
                <HubTabs
                  param="sub"
                  variant="sub"
                  items={[
                    { id: "verified", label: "Verified Player", content: <IdentityVerificationQueuePage /> },
                    // Verified Creator: application or invitation (Play Polishing 18.3).
                    { id: "creator", label: "Verified Creator", content: <ApplicationsQueuePage /> },
                    { id: "candidates", label: "Candidates (1,000 followers)", content: <CreatorCandidatesTab /> },
                    { id: "birthdays", label: "Birthday changes", content: <BirthdayChangeQueuePage /> },
                  ]}
                />
              ),
            },
            {
              id: "creators",
              label: "Creators",
              content: (
                <HubTabs
                  param="sub"
                  variant="sub"
                  items={[
                    { id: "profiles", label: "Profiles", content: <ProfilesQueuePage /> },
                    { id: "tiers", label: "Tier history", content: <TierHistoryQueuePage /> },
                  ]}
                />
              ),
            },
            {
              id: "programs",
              label: "Programs & campaigns",
              content: (
                <HubTabs
                  param="sub"
                  variant="sub"
                  items={[
                    { id: "programs", label: "Programs", content: <ProgramsQueuePage /> },
                    { id: "memberships", label: "Memberships", content: <MembershipsQueuePage /> },
                    { id: "campaigns", label: "Campaigns", content: <CampaignsQueuePage /> },
                    { id: "invitations", label: "Invitations", content: <InvitationsQueuePage /> },
                    { id: "participations", label: "Participations", content: <ParticipationsQueuePage /> },
                    { id: "brand-deals", label: "Brand deals", content: <BrandDealsQueuePage /> },
                  ]}
                />
              ),
            },
            { id: "performance", label: "Performance", content: <PerformanceQueuePage /> },
            {
              id: "payouts",
              label: "Payouts & earnings",
              ...(badges.payIssues ? { badge: badges.payIssues, badgeTone: "danger" as const } : {}),
              content: <PaymentsQueuePage />,
            },
            { id: "requirements", label: "Requirements & policies", content: <CreatorRequirementsTab /> },
          ]}
        />
      </HubEmbeddedProvider>
    </Stack>
  );
}
